import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Form, Modal, Select, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { productAPI } from '../../services/api';
import ImportExportButtons from '../../components/ImportExportButtons';

const { Search } = Input;
const { Option } = Select;

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form] = Form.useForm();

  const columns = [
    {
      title: '商品编码',
      dataIndex: 'code',
      key: 'code',
      width: 120
    },
    {
      title: '商品名称',
      dataIndex: 'name',
      key: 'name',
      width: 200
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (text) => {
        const categoryMap = {
          'milling_cutter': '铣刀',
          'drill': '钻头',
          'turning_tool': '车刀',
          'tap': '丝锥',
          'reamer': '铰刀',
          'fastener': '紧固件',
          'hand_tool': '手动工具',
          'power_tool': '电动工具',
          'measuring_tool': '量具',
          'other': '其他'
        };
        return categoryMap[text] || text;
      }
    },
    {
      title: '品牌',
      dataIndex: 'brand',
      key: 'brand',
      width: 100
    },
    {
      title: '型号',
      dataIndex: 'model',
      key: 'model',
      width: 120
    },
    {
      title: '材质',
      dataIndex: 'material',
      key: 'material',
      width: 100
    },
    {
      title: '单位',
      dataIndex: 'unit',
      key: 'unit',
      width: 60
    },
    {
      title: '单价',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 100,
      render: (text) => `¥${parseFloat(text || 0).toFixed(2)}`
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (text) => {
        const statusMap = {
          'active': '启用',
          'inactive': '停用',
          'discontinued': '停产'
        };
        return statusMap[text] || text;
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个商品吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchProducts();
  }, [pagination.current, pagination.pageSize, searchText]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getList({
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText
      });
      
      setProducts(response.data);
      setPagination({
        ...pagination,
        total: response.pagination.total
      });
    } catch (error) {
      message.error('获取商品列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleEdit = (record) => {
    setEditingProduct(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await productAPI.delete(id);
      message.success('删除成功');
      fetchProducts();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleAdd = () => {
    setEditingProduct(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingProduct) {
        await productAPI.update(editingProduct.id, values);
        message.success('更新成功');
      } else {
        await productAPI.create(values);
        message.success('创建成功');
      }
      
      setModalVisible(false);
      fetchProducts();
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写完整的表单');
      }
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>商品管理</h2>
        <Space>
          <ImportExportButtons
            type="products"
            exportParams={{ search: searchText }}
            onImportSuccess={fetchProducts}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增商品
          </Button>
        </Space>
      </div>

      <div className="search-form">
        <Search
          placeholder="搜索商品编码、名称或型号"
          onSearch={setSearchText}
          style={{ width: 300 }}
          allowClear
        />
      </div>

      <Table
        columns={columns}
        dataSource={products}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1300 }}
      />

      <Modal
        title={editingProduct ? '编辑商品' : '新增商品'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="code"
            label="商品编码"
            rules={[{ required: true, message: '请输入商品编码' }]}
          >
            <Input placeholder="请输入商品编码" />
          </Form.Item>

          <Form.Item
            name="name"
            label="商品名称"
            rules={[{ required: true, message: '请输入商品名称' }]}
          >
            <Input placeholder="请输入商品名称" />
          </Form.Item>

          <Form.Item
            name="category"
            label="商品分类"
            rules={[{ required: true, message: '请选择商品分类' }]}
          >
            <Select placeholder="请选择商品分类">
              <Option value="milling_cutter">铣刀</Option>
              <Option value="drill">钻头</Option>
              <Option value="turning_tool">车刀</Option>
              <Option value="tap">丝锥</Option>
              <Option value="reamer">铰刀</Option>
              <Option value="fastener">紧固件</Option>
              <Option value="hand_tool">手动工具</Option>
              <Option value="power_tool">电动工具</Option>
              <Option value="measuring_tool">量具</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>

          <Form.Item name="brand" label="品牌">
            <Input placeholder="请输入品牌" />
          </Form.Item>

          <Form.Item name="model" label="型号">
            <Input placeholder="请输入型号" />
          </Form.Item>

          <Form.Item name="material" label="材质">
            <Input placeholder="如: 高速钢、硬质合金等" />
          </Form.Item>

          <Form.Item name="coating" label="涂层">
            <Input placeholder="如: TiN、TiCN、TiAlN等" />
          </Form.Item>

          <Form.Item name="unit" label="单位">
            <Input placeholder="个/套/把等" defaultValue="个" />
          </Form.Item>

          <Form.Item name="unitPrice" label="单价">
            <Input type="number" prefix="¥" placeholder="请输入单价" />
          </Form.Item>

          <Form.Item name="costPrice" label="成本价">
            <Input type="number" prefix="¥" placeholder="请输入成本价" />
          </Form.Item>

          <Form.Item name="minStock" label="最低库存">
            <Input type="number" placeholder="请输入最低库存" />
          </Form.Item>

          <Form.Item name="status" label="状态">
            <Select placeholder="请选择状态" defaultValue="active">
              <Option value="active">启用</Option>
              <Option value="inactive">停用</Option>
              <Option value="discontinued">停产</Option>
            </Select>
          </Form.Item>

          <Form.Item name="description" label="商品描述">
            <Input.TextArea rows={3} placeholder="请输入商品描述" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductList;
